import { useEffect, useState, useCallback } from "react";
import { DeviceMobile, X, Share, Plus, DownloadSimple } from "@phosphor-icons/react";

// Detection helpers — kept local so the component is self-contained.
const detectPlatform = () => {
  if (typeof window === "undefined") return { ios: false, android: false, desktop: true, standalone: false };
  const ua = window.navigator.userAgent || "";
  const iosLike = /iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && "ontouchend" in document);
  const android = /Android/i.test(ua);
  // iOS Safari exposes `standalone`; other browsers use display-mode media query.
  const standalone =
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true;
  return { ios: iosLike && !android, android, desktop: !iosLike && !android, standalone };
};

// Localised home-screen step metadata keeps the modal easy to tweak later.
const IOS_STEPS = [
  { icon: Share, label: "Tap the Share button", detail: "It's the square with an arrow at the bottom of Safari." },
  { icon: Plus, label: "Tap \"Add to Home Screen\"", detail: "Scroll down in the share sheet if you don't see it." },
  { icon: DownloadSimple, label: "Tap Add", detail: "The app icon will appear on your home screen — launch it like any app." },
];

const ANDROID_STEPS = [
  { icon: Share, label: "Open the browser menu", detail: "Tap the three dots in the top-right of Chrome." },
  { icon: Plus, label: "Tap \"Install app\" or \"Add to Home Screen\"", detail: "Wording varies by Android version." },
  { icon: DownloadSimple, label: "Tap Install", detail: "You'll find the icon on your home screen / app drawer." },
];

export default function InstallAppPrompt({ variant = "button", autoShow = false, forceOpen = false, onForceClose = null }) {
  const [platform, setPlatform] = useState({ ios: false, android: false, desktop: true, standalone: false });
  const [open, setOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  // Controlled "open me" mode used by callers like NotificationsPanel
  // that want to trigger the walkthrough without rendering our own
  // Trigger button. `forceOpen` flips us open; the parent must clear it
  // via `onForceClose` when the user dismisses.
  useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen]);

  useEffect(() => {
    const p = detectPlatform();
    setPlatform(p);

    // Auto-open the modal on first mount when:
    //   1. The parent opted in via `autoShow` (we only set this on LoginPage
    //      so we don't accidentally interrupt mid-flow flows elsewhere).
    //   2. The visitor is on a phone (iOS or Android) — desktop users have
    //      no use for "Add to Home Screen" instructions.
    //   3. The app isn't already running as a PWA.
    //   4. We haven't already shown the modal on this device. Stored in
    //      localStorage so it's a one-time-per-device nudge (the user can
    //      still re-open via the pill button below). (User ask 2026-05-21
    //      "lets add the install instructions asap".)
    if (autoShow && !p.standalone && (p.ios || p.android)) {
      try {
        const dismissed = localStorage.getItem("ecDashInstallSeen");
        if (!dismissed) {
          // Wait a short beat so the modal slides in AFTER the login screen
          // finishes its entrance animation — feels intentional, not jarring.
          const t = setTimeout(() => setOpen(true), 900);
          return () => clearTimeout(t);
        }
      } catch (e) { /* localStorage blocked — silently skip auto-open */ }
    }
    return undefined;
  }, [autoShow]);

  // Mark the install nudge as seen the moment the modal closes for the
  // first time. Stored separately from the open state so closing via the
  // X button, the backdrop, or a re-render all behave identically.
  useEffect(() => {
    if (!open) return;
    return () => {
      try { localStorage.setItem("ecDashInstallSeen", "1"); } catch (e) { /* ignore */ }
    };
  }, [open]);

  useEffect(() => {
    // Chrome / Edge / Android Chrome expose this event when the PWA is installable.
    // We stash it so the user can trigger the native prompt on their timing.
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // When install finishes, hide everything.
    const onInstalled = () => {
      setDeferredPrompt(null);
      setOpen(false);
      setPlatform((p) => ({ ...p, standalone: true }));
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const triggerNativePrompt = useCallback(async () => {
    if (!deferredPrompt) return false;
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      return choice?.outcome === "accepted";
    } catch {
      return false;
    }
  }, [deferredPrompt]);

  const handleClick = async () => {
    // Android / desktop Chrome: fire the native prompt directly.
    if (deferredPrompt) {
      const accepted = await triggerNativePrompt();
      if (accepted) return;
    }
    // Everyone else (iOS, or Chrome where the event hasn't fired yet) → step-by-step modal.
    setOpen(true);
  };

  // If the app is already installed, don't pester the user — unless the
  // parent explicitly force-opened us (e.g., someone tapped "Show me how"
  // on a desktop browser to preview the iPhone instructions).
  if (platform.standalone && !forceOpen) return null;

  const steps = platform.android ? ANDROID_STEPS : IOS_STEPS;
  const title = platform.ios
    ? "Add to your iPhone Home Screen"
    : platform.android
      ? "Install on Android"
      : "Install on your phone";
  const intro = platform.ios
    ? "One-tap access from your home screen — no App Store needed. Open this page in Safari, then:"
    : platform.android
      ? "One-tap access from your home screen. In Chrome on your phone:"
      : "This app works best on your phone. Open this URL on your iPhone in Safari (or Android in Chrome), then follow the steps:";

  // Two render variants:
  //   - "button" (default): a prominent pill button, ideal for the Login page.
  //   - "footer":   a subtle inline link, ideal under the HomePage tiles.
  const Trigger =
    variant === "footer" ? (
      <button
        onClick={handleClick}
        className="flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-wider text-neutral-500 hover:text-blue-600 transition-colors py-2"
        data-testid="install-app-link"
      >
        <DeviceMobile weight="bold" className="w-3.5 h-3.5" />
        Install this app on your phone
      </button>
    ) : (
      <button
        onClick={handleClick}
        className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-white/15 hover:bg-white/25 text-xs font-bold uppercase tracking-wider text-white/85 hover:text-white transition-colors shadow-sm"
        data-testid="install-app-button"
      >
        <DeviceMobile weight="bold" className="w-3.5 h-3.5" />
        Install on iPhone / Android
      </button>
    );

  return (
    <>
      {!forceOpen && Trigger}
      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4"
          data-testid="install-modal"
          onClick={() => { setOpen(false); if (onForceClose) onForceClose(); }}
        >
          <div
            className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <DeviceMobile weight="fill" className="w-5 h-5 text-blue-600" />
                <h3 className="text-base font-bold" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
                  {title}
                </h3>
              </div>
              <button
                onClick={() => { setOpen(false); if (onForceClose) onForceClose(); }}
                className="p-1 rounded-md hover:bg-slate-100"
                aria-label="Close"
                data-testid="install-modal-close"
              >
                <X weight="bold" className="w-4 h-4" />
              </button>
            </div>

            <div className="px-5 py-5 space-y-4">
              <p className="text-sm text-slate-600 leading-relaxed">{intro}</p>

              <ol className="space-y-3">
                {steps.map((step, idx) => (
                  <li key={step.label} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-black flex items-center justify-center mt-0.5">
                      {idx + 1}
                    </span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <step.icon weight="bold" className="w-4 h-4 text-slate-600" />
                        <p className="font-semibold text-sm text-slate-900">{step.label}</p>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{step.detail}</p>
                    </div>
                  </li>
                ))}
              </ol>

              {platform.desktop && (
                <p className="text-xs text-slate-400 italic bg-slate-50 rounded-lg px-3 py-2">
                  Heads up: you're on a desktop right now. Open this URL on your phone to install.
                </p>
              )}

              <div className="pt-2">
                <button
                  onClick={() => { setOpen(false); if (onForceClose) onForceClose(); }}
                  className="w-full h-11 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm transition-colors"
                  data-testid="install-modal-done"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
